import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash, UploadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppHeader } from "@/components/AppHeader";
import { CURRENCIES, PAYMENT_TERMS } from "@/lib/poFormat";
import { api, formatApiErrorDetail } from "@/lib/api";
import { siteImg } from "@/lib/siteContent";
import { Switch } from "@/components/ui/switch";

const EMPTY = {
  name: "",
  contact: "",
  phone: "",
  email: "",
  address: "",
  currency: "USD",
  payment_terms: "",
  notes: "",
  logo: "",
  show_on_website: false,
  brands: [],
  contacts: [],
  website: "",
  showroom_address: "",
  showroom_hours: "",
  visit_notes: "",
  showroom_mode: "supplier",
  rep_name: "",
  rep_title: "",
  rep_phone: "",
  rep_email: "",
  rep_notes: "",
};
const REP_FIELDS = [
  ["rep_name", "Rep name", "Dana Whitfield"],
  ["rep_title", "Title / territory", "Outside sales · Missouri"],
  ["rep_phone", "Rep cell", "(555) 987-6543"],
  ["rep_email", "Rep email", "dana@supplier.com"],
];
const FIELDS = [
  ["name", "Supplier name", "Stone Source"],
  ["contact", "Contact person", "Maria at the desk"],
  ["phone", "Phone", "(555) 123-4567"],
  ["email", "Email", "orders@supplier.com"],
  ["address", "Address", "Street, city, state"],
];

export default function SupplierForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const brandFileRef = useRef(null);
  const [brandUpload, setBrandUpload] = useState(null);

  const brands = form.brands || [];
  const contacts = form.contacts || [];
  const setContact = (i, patch) =>
    setForm((f) => ({
      ...f,
      contacts: (f.contacts || []).map((c, j) => (j === i ? { ...c, ...patch } : c)),
    }));
  const addContact = () =>
    setForm((f) => ({ ...f, contacts: [...(f.contacts || []), { name: "", role: "", email: "" }] }));
  const removeContact = (i) =>
    setForm((f) => ({ ...f, contacts: (f.contacts || []).filter((_, j) => j !== i) }));
  const setBrand = (i, patch) =>
    setForm((f) => ({
      ...f,
      brands: (f.brands || []).map((b, j) => (j === i ? { ...b, ...patch } : b)),
    }));
  const addBrand = () =>
    setForm((f) => ({
      ...f,
      brands: [...(f.brands || []), { name: "", logo: "", show_on_website: true }],
    }));
  const removeBrand = (i) =>
    setForm((f) => ({
      ...f,
      brands: (f.brands || []).filter((_, j) => j !== i),
    }));

  const uploadBrandLogo = async (e) => {
    const file = e.target.files?.[0];
    const i = brandUpload;
    if (!file || i === null) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/site/image", fd);
      setBrand(i, { logo: data.path });
      toast.success("Brand logo uploaded");
    } catch {
      toast.error("Could not upload that image");
    }
    setUploading(false);
    setBrandUpload(null);
    if (brandFileRef.current) brandFileRef.current.value = "";
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/site/image", fd);
      setForm((f) => ({ ...f, logo: data.path }));
      toast.success("Logo uploaded");
    } catch {
      toast.error("Could not upload that image");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const load = useCallback(async () => {
    if (!id) return;
    const { data } = await api.get(`/suppliers/${id}`);
    setForm({ ...EMPTY, ...data });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = id
        ? await api.put(`/suppliers/${id}`, form)
        : await api.post("/suppliers", form);
      toast.success(id ? "Supplier updated" : "Supplier added");
      navigate(`/inventory/suppliers/${data.id}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save supplier",
      );
    }
    setBusy(false);
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader
        title={id ? "Inventory / Edit Supplier" : "Inventory / New Supplier"}
      />
      <main className="relative z-10 pb-28 pt-6">
        <Link
          to="/inventory/suppliers"
          data-testid="back-to-suppliers"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> All suppliers
        </Link>
        <h1 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
          {id ? "Edit Supplier" : "New Supplier"}
        </h1>

        <form
          onSubmit={submit}
          data-testid="supplier-form"
          className="mt-6 max-w-2xl space-y-6"
        >
          <div className="grid gap-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4 sm:grid-cols-2">
            {FIELDS.map(([k, label, ph]) => (
              <div
                key={k}
                className={`space-y-2 ${k === "address" ? "sm:col-span-2" : ""}`}
              >
                <Label className="text-xs uppercase tracking-widest text-zinc-400">
                  {label}
                </Label>
                <Input
                  data-testid={`sup-${k}-input`}
                  required={k === "name"}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  placeholder={ph}
                  className="h-12 border-zinc-800 bg-zinc-950 text-base"
                />
              </div>
            ))}
          </div>
          <div className="grid gap-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Default currency
              </Label>
              <Select
                value={form.currency || "USD"}
                onValueChange={(v) => setForm({ ...form, currency: v })}
              >
                <SelectTrigger
                  data-testid="sup-currency-select"
                  className="h-12 border-zinc-800 bg-zinc-950 text-base"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {CURRENCIES.map(([code, label]) => (
                    <SelectItem key={code} value={code}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Payment terms
              </Label>
              <Select
                value={form.payment_terms || "none"}
                onValueChange={(v) =>
                  setForm({ ...form, payment_terms: v === "none" ? "" : v })
                }
              >
                <SelectTrigger
                  data-testid="sup-terms-select"
                  className="h-12 border-zinc-800 bg-zinc-950 text-base"
                >
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  <SelectItem value="none">—</SelectItem>
                  {PAYMENT_TERMS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div
            className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            data-testid="sup-rep-panel"
          >
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Sales rep — who we call
              </Label>
              <p className="mt-1 text-xs text-zinc-500">
                Your account rep for pricing, availability and chasing material. Staff only —
                never shown on the website.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {REP_FIELDS.map(([k, label, ph]) => (
                <div key={k} className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-400">
                    {label}
                  </Label>
                  <Input
                    data-testid={`sup-${k.replace("_", "-")}-input`}
                    type={k === "rep_email" ? "email" : "text"}
                    value={form[k] || ""}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    placeholder={ph}
                    className="h-11 border-zinc-800 bg-zinc-950"
                  />
                </div>
              ))}
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-400">
                  Rep notes
                </Label>
                <Textarea
                  data-testid="sup-rep-notes-input"
                  rows={2}
                  value={form.rep_notes || ""}
                  onChange={(e) => setForm({ ...form, rep_notes: e.target.value })}
                  placeholder="Best reached by text · covers MO & IL · backup: inside sales desk"
                  className="border-zinc-800 bg-zinc-950"
                />
              </div>
            </div>
          </div>
          <div className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4" data-testid="sup-contacts-panel">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">
                  Team contacts
                </Label>
                <p className="mt-1 text-xs text-zinc-500">
                  Extra people on the supplier's side (purchasing, A/P, your rep). Each one becomes a
                  tap-to-add chip in the Email popups for purchase orders and returns.
                </p>
              </div>
              <Button
                type="button"
                data-testid="sup-add-contact"
                variant="outline"
                onClick={addContact}
                className="h-10 shrink-0 border-zinc-700 bg-transparent text-xs font-bold uppercase tracking-widest"
              >
                <Plus size={14} weight="bold" className="mr-1.5" /> Add contact
              </Button>
            </div>
            {contacts.length === 0 && (
              <p className="text-sm text-zinc-500" data-testid="sup-contacts-empty">
                No extra contacts yet — the main email and sales rep above are always offered.
              </p>
            )}
            <div className="space-y-2" data-testid="sup-contacts-list">
              {contacts.map((c, i) => (
                <div
                  key={i}
                  data-testid={`sup-contact-row-${i}`}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950 p-2"
                >
                  <Input
                    data-testid={`sup-contact-name-${i}`}
                    value={c.name || ""}
                    onChange={(e) => setContact(i, { name: e.target.value })}
                    placeholder="Name"
                    className="h-10 min-w-[8rem] flex-1 border-zinc-800 bg-zinc-900 text-sm"
                  />
                  <Input
                    data-testid={`sup-contact-role-${i}`}
                    value={c.role || ""}
                    onChange={(e) => setContact(i, { role: e.target.value })}
                    placeholder="Role (e.g. A/P)"
                    className="h-10 min-w-[7rem] flex-1 border-zinc-800 bg-zinc-900 text-sm"
                  />
                  <Input
                    data-testid={`sup-contact-email-${i}`}
                    type="email"
                    value={c.email || ""}
                    onChange={(e) => setContact(i, { email: e.target.value })}
                    placeholder="name@supplier.com"
                    className="h-10 min-w-[10rem] flex-[2] border-zinc-800 bg-zinc-900 text-sm"
                  />
                  <button
                    type="button"
                    data-testid={`sup-contact-remove-${i}`}
                    onClick={() => removeContact(i)}
                    className="rounded p-2 text-zinc-500 hover:bg-red-950/40 hover:text-red-400"
                    aria-label="Remove contact"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Notes
            </Label>
            <Textarea
              data-testid="sup-notes-input"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Lead times, minimums, rep notes…"
              className="border-zinc-800 bg-zinc-950"
            />
          </div>
          <div
            className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            data-testid="sup-showroom-panel"
          >
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                See it in person — public showroom details
              </Label>
              <p className="mt-1 text-xs text-zinc-500">
                Shown on the website only for <strong>special-order</strong> colors
                (and untracked supplier stock) sourced from this supplier, so shoppers know where to view a full slab.
                Leave the address blank to use the main address above.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-400">
                  Show on the website as
                </Label>
                <Select
                  value={form.showroom_mode || "supplier"}
                  onValueChange={(v) => setForm({ ...form, showroom_mode: v })}
                >
                  <SelectTrigger
                    data-testid="sup-showroom-mode-select"
                    className="h-11 border-zinc-800 bg-zinc-950"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">
                    <SelectItem value="supplier" data-testid="sup-showroom-mode-supplier">
                      This supplier's showroom (address, hours, map below)
                    </SelectItem>
                    <SelectItem value="ours" data-testid="sup-showroom-mode-ours">
                      Our showroom instead (Company settings address & hours)
                    </SelectItem>
                    <SelectItem value="hidden" data-testid="sup-showroom-mode-hidden">
                      Hidden — no "See it in person" box for this supplier's slabs
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-400">
                  Showroom address
                </Label>
                <Input
                  data-testid="sup-showroom-address-input"
                  value={form.showroom_address || ""}
                  onChange={(e) => setForm({ ...form, showroom_address: e.target.value })}
                  placeholder="Same as address above if blank"
                  className="h-11 border-zinc-800 bg-zinc-950"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-400">
                  Website
                </Label>
                <Input
                  data-testid="sup-website-input"
                  value={form.website || ""}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://supplier.com"
                  className="h-11 border-zinc-800 bg-zinc-950"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-400">
                  Showroom hours
                </Label>
                <Input
                  data-testid="sup-showroom-hours-input"
                  value={form.showroom_hours || ""}
                  onChange={(e) => setForm({ ...form, showroom_hours: e.target.value })}
                  placeholder="Mon–Fri 8–5, Sat 9–1"
                  className="h-11 border-zinc-800 bg-zinc-950"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-400">
                  Visiting notes
                </Label>
                <Textarea
                  data-testid="sup-visit-notes-input"
                  rows={2}
                  value={form.visit_notes || ""}
                  onChange={(e) => setForm({ ...form, visit_notes: e.target.value })}
                  placeholder="Ask for the Brioni Granite account · by appointment · bring your sample"
                  className="border-zinc-800 bg-zinc-950"
                />
              </div>
            </div>
          </div>
          <div className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">
                  Brands this supplier carries
                </Label>
                <p className="mt-1 text-xs text-zinc-500">
                  e.g. MSI → Q Premium, Stile. Brands become a pick-list on slabs
                  and sinks, and replace the supplier's own name in the website
                  “Brands we stock” band.
                </p>
              </div>
              <Button
                type="button"
                data-testid="sup-add-brand"
                variant="outline"
                onClick={addBrand}
                className="h-10 shrink-0 border-zinc-700 bg-transparent text-xs font-bold uppercase tracking-widest"
              >
                <Plus size={14} weight="bold" className="mr-1.5" /> Add brand
              </Button>
            </div>
            <input
              ref={brandFileRef}
              type="file"
              accept="image/*"
              onChange={uploadBrandLogo}
              className="hidden"
              data-testid="sup-brand-logo-file"
            />
            {brands.length === 0 && (
              <p
                className="text-sm text-zinc-500"
                data-testid="sup-brands-empty"
              >
                No brands yet — the supplier's own name is used.
              </p>
            )}
            <div className="space-y-2" data-testid="sup-brands-list">
              {brands.map((b, i) => (
                <div
                  key={i}
                  data-testid={`sup-brand-row-${i}`}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950 p-2"
                >
                  <div className="flex h-10 w-16 shrink-0 items-center justify-center rounded bg-white p-1">
                    {b.logo ? (
                      <img
                        src={siteImg(b.logo)}
                        alt={b.name || "Brand"}
                        data-testid={`sup-brand-logo-preview-${i}`}
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-400">
                        Text
                      </span>
                    )}
                  </div>
                  <Input
                    data-testid={`sup-brand-name-${i}`}
                    value={b.name}
                    onChange={(e) => setBrand(i, { name: e.target.value })}
                    placeholder="Brand name"
                    className="h-10 min-w-[8rem] flex-1 border-zinc-800 bg-zinc-900 text-sm"
                  />
                  <Input
                    data-testid={`sup-brand-logo-${i}`}
                    value={b.logo || ""}
                    onChange={(e) => setBrand(i, { logo: e.target.value })}
                    placeholder="Logo link (optional)"
                    className="h-10 min-w-[8rem] flex-1 border-zinc-800 bg-zinc-900 text-sm"
                  />
                  <Button
                    type="button"
                    data-testid={`sup-brand-upload-${i}`}
                    variant="outline"
                    disabled={uploading}
                    onClick={() => {
                      setBrandUpload(i);
                      brandFileRef.current?.click();
                    }}
                    className="h-10 border-zinc-700 bg-transparent px-3 text-[11px] uppercase tracking-widest"
                  >
                    <UploadSimple size={14} weight="bold" />
                  </Button>
                  <label
                    className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-400"
                    title="Publish this brand in the website band"
                  >
                    Web
                    <Switch
                      data-testid={`sup-brand-web-${i}`}
                      checked={b.show_on_website !== false}
                      onCheckedChange={(v) => setBrand(i, { show_on_website: v })}
                    />
                  </label>
                  <button
                    type="button"
                    data-testid={`sup-brand-remove-${i}`}
                    onClick={() => removeBrand(i)}
                    className="rounded p-2 text-zinc-500 hover:bg-red-950/40 hover:text-red-400"
                    aria-label="Remove brand"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Supplier logo &amp; website
            </Label>
            <p className="text-xs text-zinc-500">
              A transparent PNG or SVG works best — it's displayed in white on
              the dark “Brands we stock” band. Turn on “Show on website” below to
              publish this supplier (its brands above, when it has any).
            </p>
            <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-zinc-200">
                  Show on website
                </p>
                <p className="text-xs text-zinc-500">
                  Publish this supplier's brands (or its name) in the public
                  “Brands we stock” band.
                </p>
              </div>
              <Switch
                data-testid="sup-show-website"
                checked={!!form.show_on_website}
                onCheckedChange={(v) =>
                  setForm({ ...form, show_on_website: v })
                }
              />
            </div>
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex h-28 w-40 shrink-0 items-center justify-center rounded-md border border-zinc-800 bg-white p-3">
                {form.logo ? (
                  <img
                    src={siteImg(form.logo)}
                    alt="Logo preview"
                    data-testid="sup-logo-preview"
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                    Default
                  </span>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <Input
                  data-testid="sup-logo-input"
                  value={form.logo}
                  onChange={(e) => setForm({ ...form, logo: e.target.value })}
                  placeholder="Paste an image link, or upload"
                  className="h-12 border-zinc-800 bg-zinc-950 text-base"
                />
                <div className="flex gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={uploadLogo}
                    className="hidden"
                    data-testid="sup-logo-file"
                  />
                  <Button
                    type="button"
                    data-testid="sup-logo-upload"
                    variant="outline"
                    disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                    className="h-11 flex-1 border-zinc-700 bg-transparent text-xs font-bold uppercase tracking-widest"
                  >
                    <UploadSimple size={15} weight="bold" className="mr-2" />
                    {uploading ? "Uploading…" : "Upload"}
                  </Button>
                  {form.logo && (
                    <Button
                      type="button"
                      data-testid="sup-logo-clear"
                      variant="outline"
                      onClick={() => setForm({ ...form, logo: "" })}
                      className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              type="submit"
              data-testid="save-supplier-button"
              disabled={busy}
              className="h-12 flex-1 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              {busy ? "Saving…" : id ? "Save Supplier" : "Add Supplier"}
            </Button>
            <Button
              type="button"
              data-testid="cancel-supplier-button"
              variant="outline"
              onClick={() => navigate("/inventory/suppliers")}
              className="h-12 border-zinc-700 bg-transparent text-xs uppercase tracking-widest"
            >
              Cancel
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
